import { generateCombination } from '@14ch/color-palette-generator';
import chroma from 'chroma-js';
import { formatHex } from 'culori';


function extractThemeColors(paletteConfig) {
    const primary = paletteConfig.find(p => p.id === "primary");
    const secondary = paletteConfig.find(p => p.id === "secondary");
    const accent = paletteConfig.find(p => p.id === "secondary2");
    
    const lightnessMap = {
      50: 0.98, 100: 0.95, 200: 0.88, 300: 0.78, 400: 0.65,
      500: 0.55, 600: 0.45, 700: 0.38, 800: 0.30, 900: 0.23, 950: 0.15
    };
    
    const getShade = (oklchSeed, targetLevel) => {
      return formatHex({
        mode: 'oklch',
        l: lightnessMap[targetLevel],
        c: oklchSeed.c,
        h: oklchSeed.h
      });
    };
    
    return {
      light: {
        primary: getShade(primary.seedOklch, 500),
        secondary: getShade(secondary.seedOklch, 500),
        accent: getShade(accent.seedOklch, 500)
      },
      dark: {
        primary: getShade(primary.seedOklch, 400),
        secondary: getShade(secondary.seedOklch, 400),
        accent: getShade(accent.seedOklch, 400)
      }
    };
}

export function generateTheme(primaryColor, isUsingDarkMode) {

    const combination = generateCombination({
        seedColor: primaryColor,
        combinationType: "triadic",
        lightnessMethod: "hybrid",
        baseColorStrategy: "harmonic",
    });

    const themeColors = extractThemeColors(combination);

    console.log(`Combination: ${JSON.stringify(themeColors)}`);

    // Helper: Convert to DaisyUI HSL format
    const toHSL = (color) => {
      const [h, s, l] = chroma(color).hsl();
      return `${Math.round(h || 0)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    // Helper: Get contrasting text color
    const getContentColor = (bgColor) => {
      const bg = chroma(bgColor);
      const white = chroma('#FFF');
      const black = chroma('#000');
      return chroma.contrast(bg, white) > chroma.contrast(bg, black) ? white : black;
    };
    
    // Generate colors
    const primary = chroma(isUsingDarkMode ? themeColors.dark.primary : themeColors.light.primary);
    
    const secondary = chroma(isUsingDarkMode ? themeColors.dark.secondary : themeColors.light.secondary);
    
    const accent = chroma(isUsingDarkMode ? themeColors.dark.accent : themeColors.light.accent);
    
    // Generate focus variants (slightly darker/lighter)
    const primaryFocus = primary.luminance() > 0.5 
      ? primary.darken(0.2) 
      : primary.brighten(0.2);
      
    const secondaryFocus = secondary.luminance() > 0.5 
      ? secondary.darken(0.2) 
      : secondary.brighten(0.2);
      
    const accentFocus = accent.luminance() > 0.5 
      ? accent.darken(0.2) 
      : accent.brighten(0.2);
    
    // Return full theme in DaisyUI CSS variable format
    return {
      // Primary
      '--p': toHSL(primary),
      '--pf': toHSL(primaryFocus),
      '--pc': toHSL(getContentColor(primary)),
      
      // Secondary
      '--s': toHSL(secondary),
      '--sf': toHSL(secondaryFocus),
      '--sc': toHSL(getContentColor(secondary)),
      
      // Accent
      '--a': toHSL(accent),
      '--af': toHSL(accentFocus),
      '--ac': toHSL(getContentColor(accent)),
      
    };
}
  
  // Apply to DOM
export function applyTheme(primaryColor) {
    let isUsingDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
    const theme = generateTheme(primaryColor, isUsingDarkMode);
    const root = document.documentElement;
    
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
}